import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, CheckCircle2, MessageCircle } from "lucide-react";
import LexicalRenderer from "@/components/blog/LexicalRenderer";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/site/Breadcrumbs";
import CountryCoverage from "@/components/services/CountryCoverage";
import {
  useGuideArticle,
  fetchGuideRelatedArticles,
  fetchGuideRelatedServices,
} from "@/lib/guidesApi";
import { signalPrerenderReady } from "@/lib/prerenderReady";
import DynamicRouteLoader from "@/components/site/DynamicRouteLoader";
import { useSeoMetadata } from "@/hooks/useSeoMetadata";
import { trackCtaClick, trackEvent } from "@/lib/ga";

/**
 * GuideArticle — Pillar Guide detail page.
 *
 * Premium Service-page feel (NOT a blog article):
 *   HERO (eyebrow + H1 + value prop + primary CTA above the fold)
 *   ↓
 *   Service Overview   (`problemStatement`)
 *   ↓
 *   Business Benefits  (`benefits[]`)
 *   ↓
 *   How It Works       (`process[]`)
 *   ↓
 *   Long-form body     (Lexical rich content — the deep dive)
 *   ↓
 *   FAQ                (`faq[]`)
 *   ↓
 *   Related Articles   (image + title + excerpt cards)
 *   ↓
 *   Related Services   (link cards)
 *   ↓
 *   Final "Discuss this Service" conversion CTA
 */
export default function GuideArticle() {
  const { slug } = useParams();
  const { loading, error, notFound, data: guide } = useGuideArticle(slug);
  const { data: seoMeta } = useSeoMetadata();
  const [related, setRelated] = useState([]);
  const [relatedServices, setRelatedServices] = useState([]);

  useEffect(() => {
    if (guide?.id) {
      fetchGuideRelatedArticles(guide.id).then(setRelated).catch(() => setRelated([]));
      fetchGuideRelatedServices(guide.id).then(setRelatedServices).catch(() => setRelatedServices([]));
    }
  }, [guide?.id]);

  // Prerender.io-compatible readiness signal (see /lib/prerenderReady.js
  // and /app/docs/SEO_CONTENT_BIBLE.md §Prerender rules).
  useEffect(() => {
    if (loading) return;
    if (notFound) { signalPrerenderReady("notFound"); return; }
    if (error || !guide) { signalPrerenderReady("error"); return; }
    signalPrerenderReady("ok");
  }, [loading, error, notFound, guide]);

  // Loading state: MUST NOT mount a page-shell (<main>Loading…</main>)
  // as the first child of #root. See /app/docs/SEO_CONTENT_BIBLE.md §4.5.
  if (loading) {
    // Bing "Add a title tag" fix: renderer may snapshot before data
    // loads. Emit a slug-derived, non-empty <title> + canonical so the
    // captured HTML is never title-less. Real SEO metadata (from
    // useSeoMetadata) still overrides this once the guide resolves.
    const _loadingTitle = _slugToTitle(slug);
    return (
      <>
        <Helmet>
          <title>{`${_loadingTitle} — Manufacturing and Trading Services`}</title>
          <link rel="canonical" href={_absUrl(`/guides/${slug}`)} />
        </Helmet>
        <DynamicRouteLoader variant="loading" testId="guide-loading" />
      </>
    );
  }
  // Confirmed 404 from API — guide genuinely does not exist. Emit noindex.
  if (notFound) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-16" data-testid="guide-not-found">
        <Helmet>
          <title>Guide not found — Manufacturing and Trading Services</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <h1 className="text-2xl font-semibold text-neutral-900 mb-4">Guide not found</h1>
        <Link to="/services" className="text-neutral-600 hover:text-neutral-900">
          ← Back to Services
        </Link>
      </main>
    );
  }
  // Transient error (network / 5xx / timeout) after retry — guide MAY
  // exist; we just failed to load it. Do NOT emit noindex — crawlers
  // and prerenderers should not cache a permanent "hide from index" for
  // an otherwise valid URL. Google will re-crawl later.
  // Transient error: same guarantee — no <main> shell in #root. Marker
  // stays on the portal element so smart-poll refuses to flip ready.
  // See /app/docs/SEO_CONTENT_BIBLE.md §4.5.
  if (error || !guide) {
    return <DynamicRouteLoader variant="unavailable" testId="guide-unavailable" />;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const seo = guide.seo || {};
  const adv = guide.seoAdvanced || {};
  const canonical = _absUrl(seo.canonicalPath || `/guides/${guide.slug}`);
  const metaTitle = seo.metaTitle || guide.title;
  const metaDescription = seo.metaDescription || guide.excerpt || "";
  const ogImage = _absUrl(seo.ogImage?.url || guide.featuredImage?.url) || seoMeta?.logoUrl;
  const twitterImage = _absUrl(seo.twitterImage?.url || seo.ogImage?.url || guide.featuredImage?.url) || seoMeta?.logoUrl;

  const service = guide.service && typeof guide.service === "object" ? guide.service : null;
  const contactHref = service
    ? `/contact?topic=${encodeURIComponent(service.title || "")}&source=${encodeURIComponent(`guide/${guide.slug}`)}`
    : `/contact?source=${encodeURIComponent(`guide/${guide.slug}`)}`;
  const ctaLabel = guide.ctaText || "Discuss this Service";

  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/services" },
    ...(service ? [{ name: service.title, href: `/services#${service.slug}` }] : []),
  ];
  const breadcrumbItemsForSchema = [...breadcrumbItems, { name: guide.title }];

  // JSON-LD schemas
  const articleSchema = adv.articleSchema || buildArticleSchema({
    guide, canonical, ogImage, seoMeta, description: metaDescription,
  });
  const breadcrumbSchema = adv.breadcrumbSchema || buildBreadcrumbSchema(breadcrumbItemsForSchema, origin);
  const faqSchema = adv.faqSchema || buildFaqSchema(guide.faq);
  const jsonLd = [articleSchema, breadcrumbSchema, faqSchema].filter(Boolean);

  const heroValueProp = guide.excerpt || guide.problemStatement || "";
  const hasProblemBelowHero =
    guide.problemStatement && guide.excerpt && guide.problemStatement !== guide.excerpt;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={seo.robots || "index,follow"} />
        <meta property="og:title" content={seo.ogTitle || metaTitle} />
        {(seo.ogDescription || metaDescription) && (
          <meta property="og:description" content={seo.ogDescription || metaDescription} />
        )}
        <meta property="og:type" content={seo.ogType || "article"} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={seo.ogLocale || "en_US"} />
        <meta property="og:site_name" content={seo.ogSiteName || seoMeta?.displayName || "Manufacturing & Trading Services"} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {guide.publishedAt && <meta property="article:published_time" content={guide.publishedAt} />}
        {guide.updatedAt && <meta property="article:modified_time" content={guide.updatedAt} />}
        <meta name="twitter:card" content={seo.twitterCard || "summary_large_image"} />
        <meta name="twitter:title" content={seo.twitterTitle || seo.ogTitle || metaTitle} />
        {(seo.twitterDescription || seo.ogDescription || metaDescription) && (
          <meta name="twitter:description" content={seo.twitterDescription || seo.ogDescription || metaDescription} />
        )}
        {twitterImage && <meta name="twitter:image" content={twitterImage} />}
        {jsonLd.map((schema, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      <main data-testid="guide-article">
        {/* HERO ============================================================ */}
        <GuideHero
          guide={guide}
          service={service}
          valueProp={heroValueProp}
          contactHref={contactHref}
          ctaLabel={ctaLabel}
          breadcrumbItems={breadcrumbItems}
        />

        {/* Featured hero image — wide banner between the text hero and
            the body. Present only when the CMS has attached one; the
            layout stays fully valid without it. */}
        {guide.featuredImage?.url && (
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 -mt-4 md:-mt-6" data-testid="guide-hero-image">
            <div className="aspect-[16/7] overflow-hidden rounded-3xl bg-neutral-100 border border-[#ececef]">
              <img
                src={guide.featuredImage.url}
                alt={guide.featuredImage.alt || guide.title}
                width={guide.featuredImage.width || 1600}
                height={guide.featuredImage.height || 700}
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* BODY ============================================================ */}
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          {/* Service Overview — problem framing, only when it differs from the hero excerpt */}
          {hasProblemBelowHero && (
            <ServiceOverview text={guide.problemStatement} serviceTitle={service?.title} />
          )}

          {/* Business Benefits */}
          {Array.isArray(guide.benefits) && guide.benefits.length > 0 && (
            <BenefitsBlock benefits={guide.benefits} />
          )}

          {/* How It Works */}
          {Array.isArray(guide.process) && guide.process.length > 0 && (
            <ProcessBlock steps={guide.process} />
          )}

          {/* Long-form body (deep dive) */}
          {guide.content && (
            <section className="mt-16 pt-16 border-t border-neutral-200" data-testid="guide-content">
              <LexicalRenderer content={guide.content} />
            </section>
          )}

          {/* FAQ */}
          {Array.isArray(guide.faq) && guide.faq.length > 0 && (
            <FaqBlock items={guide.faq} />
          )}

          {/* Explore by country — pan-Asian coverage nav */}
          <CountryCoverage variant="inline" source="guide" showChina={false} />
        </div>

        {/* Related Articles — full-width off-white band */}
        {related.length > 0 && (
          <RelatedArticlesBlock articles={related} guideSlug={guide.slug} />
        )}

        {/* Related Services */}
        {relatedServices.length > 0 && (
          <RelatedServicesBlock services={relatedServices} guideSlug={guide.slug} />
        )}

        {/* Final conversion CTA — dark full-bleed panel */}
        <FinalCta
          href={contactHref}
          label={ctaLabel}
          serviceTitle={service?.title}
          serviceSlug={service?.slug}
          guideSlug={guide.slug}
        />
      </main>
    </>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────

function GuideHero({ guide, service, valueProp, contactHref, ctaLabel, breadcrumbItems }) {
  return (
    <section className="border-b border-[#ececef] mts-soft-gradient relative overflow-hidden" data-testid="guide-hero">
      <div
        aria-hidden
        className="absolute inset-0 mts-dot-grid opacity-30 [mask-image:radial-gradient(60%_60%_at_50%_50%,black,transparent)]"
      />
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
        <Breadcrumbs items={breadcrumbItems} className="mb-8 text-[13px] text-[#6e6e73]" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]" data-testid="guide-eyebrow">
              Pillar Guide{service?.title ? ` · ${service.title}` : ""}
            </p>
            <h1
              className="mt-6 text-[38px] sm:text-[52px] md:text-[68px] font-semibold tracking-[-0.03em] leading-[1.02] text-[#0a0a0a] text-balance"
              data-testid="guide-h1"
            >
              {guide.displayTitle || guide.title}
            </h1>
            {valueProp && (
              <p
                className="mt-6 text-[17px] md:text-[19px] leading-relaxed text-[#424245] max-w-2xl text-pretty"
                data-testid="guide-hero-value-prop"
              >
                {valueProp}
              </p>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="guide-hero-cta-row">
              <Link
                to={contactHref}
                onClick={() =>
                  trackCtaClick(ctaLabel, `guide-hero:${guide.slug}`, {
                    service_slug: service?.slug || "",
                    guide_slug: guide.slug,
                    cta_position: "hero",
                  })
                }
                data-testid="guide-hero-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#0a0a0a] text-white px-6 py-3 text-[14px] font-medium hover:opacity-90 transition-opacity"
              >
                <MessageCircle size={16} strokeWidth={1.75} />
                {ctaLabel}
              </Link>
              {guide.readingTime && (
                <span className="text-[13px] text-[#86868b]" data-testid="guide-reading-time">
                  {guide.readingTime} min read
                </span>
              )}
            </div>
          </div>

          {/* Right column: highlight card — Service value or tags */}
          {service && (
            <div className="md:col-span-4">
              <div className="rounded-3xl border border-[#ececef] bg-white/70 backdrop-blur p-6" data-testid="guide-hero-side">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                  The service
                </p>
                <p className="mt-3 text-[16px] font-medium text-[#0a0a0a] leading-snug">
                  {service.title}
                </p>
                {service.summary && (
                  <p className="mt-3 text-[13.5px] leading-relaxed text-[#6e6e73]">
                    {service.summary}
                  </p>
                )}
                <Link
                  to={`/services#${service.slug}`}
                  className="mt-5 inline-flex items-center gap-1 text-[13px] font-medium text-[#0a0a0a] hover:opacity-70 transition-opacity"
                  data-testid="guide-hero-service-link"
                >
                  See on Services page <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Structured sections ─────────────────────────────────────────────────

function ServiceOverview({ text, serviceTitle }) {
  return (
    <section className="mb-14" data-testid="guide-overview">
      <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
        Service overview
      </p>
      <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight">
        {serviceTitle ? `Why ${serviceTitle} matters` : "Why this matters"}
      </h2>
      <p className="mt-4 text-[17px] leading-relaxed text-neutral-800">
        {text}
      </p>
    </section>
  );
}

function BenefitsBlock({ benefits }) {
  return (
    <section className="mb-14" data-testid="guide-benefits">
      <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
        Business benefits
      </p>
      <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight mb-8">
        What you gain from this service
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {benefits.map((b, i) => (
          <div
            key={i}
            className="rounded-2xl border border-neutral-200 p-5 bg-white"
            data-testid={`guide-benefit-${i}`}
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 size={18} className="mt-0.5 text-neutral-900 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="text-[15px] font-semibold text-neutral-900 leading-snug">
                  {b.title}
                </div>
                {b.description && (
                  <div className="mt-2 text-[14px] leading-relaxed text-neutral-600">
                    {b.description}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProcessBlock({ steps }) {
  return (
    <section className="mb-14" data-testid="guide-process">
      <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
        How it works
      </p>
      <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight mb-8">
        End-to-end process
      </h2>
      <ol className="space-y-5">
        {steps.map((s, i) => (
          <li
            key={i}
            className="flex gap-5"
            data-testid={`guide-process-step-${i}`}
          >
            <div className="shrink-0 w-8 h-8 rounded-full bg-neutral-900 text-white text-sm font-semibold flex items-center justify-center">
              {i + 1}
            </div>
            <div className="pt-0.5">
              <div className="text-[15px] font-semibold text-neutral-900 leading-snug">
                {s.title}
              </div>
              {s.description && (
                <div className="mt-1.5 text-[14px] leading-relaxed text-neutral-600">
                  {s.description}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function FaqBlock({ items }) {
  return (
    <section className="mt-16 pt-16 border-t border-neutral-200" data-testid="guide-faq">
      <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
        FAQ
      </p>
      <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight mb-8">
        Frequently asked questions
      </h2>
      <div className="space-y-4">
        {items.map((f, i) => (
          <details
            key={i}
            className="rounded-2xl border border-neutral-200 p-5 bg-white group"
            data-testid={`guide-faq-item-${i}`}
          >
            <summary className="cursor-pointer text-[15px] font-semibold text-neutral-900 flex items-start justify-between gap-4 list-none">
              <span>{f.question}</span>
              <span className="text-neutral-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
            </summary>
            <p className="mt-3 text-[14px] leading-relaxed text-neutral-700">
              {f.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

function RelatedArticlesBlock({ articles, guideSlug }) {
  return (
    <section className="bg-[#f7f7f8] border-t border-b border-[#ececef]" data-testid="guide-related-articles">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
            Related reading
          </p>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight">
            Continue your research
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((a) => (
            <RelatedArticleCard key={a.id} article={a} guideSlug={guideSlug} />
          ))}
        </div>
      </div>
    </section>
  );
}

function RelatedArticleCard({ article, guideSlug }) {
  const img = article?.featuredImage?.url;
  return (
    <Link
      to={`/blog/${article.slug}`}
      onClick={() =>
        trackEvent("related_article_click", {
          article_slug: article.slug,
          guide_slug: guideSlug || "",
        })
      }
      className="group flex flex-col h-full rounded-2xl overflow-hidden bg-white border border-neutral-200 hover:border-neutral-900 transition-colors"
      data-testid={`guide-related-article-${article.slug}`}
    >
      <div className="aspect-video overflow-hidden bg-neutral-100">
        {img ? (
          <img
            src={img}
            alt={article?.featuredImage?.alt || article.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300">
            <span className="text-[11px] uppercase tracking-[0.15em]">Article</span>
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-[16px] font-semibold text-neutral-900 leading-snug line-clamp-2 group-hover:underline underline-offset-4">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="mt-2 text-[13.5px] leading-relaxed text-neutral-600 line-clamp-3">
            {article.excerpt}
          </p>
        )}
        <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-neutral-900">
          Read more <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function RelatedServicesBlock({ services, guideSlug }) {
  return (
    <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="guide-related-services">
      <div className="max-w-3xl">
        <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
          Also from MTS
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight">
          Related services
        </h2>
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
        {services.map((s) => (
          <Link
            key={s.id || s.slug}
            to={`/services#${s.slug}`}
            onClick={() =>
              trackEvent("related_service_click", {
                service_slug: s.slug,
                guide_slug: guideSlug || "",
              })
            }
            className="group block rounded-2xl border border-neutral-200 p-6 hover:border-neutral-900 transition-colors bg-white"
            data-testid={`guide-related-service-${s.slug}`}
          >
            <div className="font-semibold text-neutral-900 mb-2 line-clamp-2">
              {s.title}
            </div>
            {s.summary && (
              <div className="text-sm text-neutral-600 line-clamp-3">
                {s.summary}
              </div>
            )}
            <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-neutral-900">
              Explore <ArrowUpRight size={14} />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FinalCta({ href, label, serviceTitle, serviceSlug, guideSlug }) {
  return (
    <section className="relative overflow-hidden" data-testid="guide-final-cta">
      <div className="mts-dark-panel">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                Ready to move
              </p>
              <h2 className="mt-6 text-[36px] sm:text-[48px] md:text-[60px] font-semibold tracking-[-0.03em] text-white leading-[1.05] max-w-3xl text-balance">
                {serviceTitle
                  ? <>Let's turn this into an execution plan for <span className="italic font-normal text-white/90" style={{ fontFamily: "'Instrument Serif', serif" }}>{serviceTitle}</span>.</>
                  : <>Let's turn this into an execution plan.</>}
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-white/70 max-w-xl">
                Send us your requirements and we'll come back with a concrete
                scope, timeline, and cost estimate — no long sales cycle.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to={href}
                onClick={() =>
                  trackCtaClick(label, `guide-final:${guideSlug}`, {
                    service_slug: serviceSlug || "",
                    guide_slug: guideSlug,
                    cta_position: "final",
                  })
                }
                data-testid="guide-final-cta-button"
                className="mts-btn-inverse"
              >
                {label} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function _absUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  try {
    return new URL(pathOrUrl, window.location.origin).toString();
  } catch {
    return pathOrUrl;
  }
}

// Derives a human-readable, non-empty title from a route slug for the
// loading-state <Helmet>. Strips a trailing short hex hash suffix and
// Title-Cases the remaining words. Used only until real SEO metadata
// loads; success branch's Helmet takes over on data resolution.
function _slugToTitle(slug) {
  if (!slug) return "Guide";
  return String(slug)
    .replace(/-[0-9a-f]{6,10}$/i, "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || "Guide";
}

function buildArticleSchema({ guide, canonical, ogImage, seoMeta, description }) {
  const orgId = seoMeta?.baseURL ? `${seoMeta.baseURL}/#organization` : undefined;
  const websiteId = seoMeta?.baseURL ? `${seoMeta.baseURL}/#website` : undefined;
  const orgRef = orgId
    ? { "@id": orgId }
    : { "@type": "Organization", name: seoMeta?.displayName || "Manufacturing and Trading Services" };
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": canonical ? `${canonical}#article` : undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    headline: guide.title,
    description: description || undefined,
    image: ogImage ? [ogImage] : undefined,
    author: orgRef,
    publisher: orgRef,
    isPartOf: websiteId ? { "@id": websiteId } : undefined,
    datePublished: guide.publishedAt || guide.createdAt || undefined,
    dateModified: guide.updatedAt || guide.publishedAt || undefined,
    inLanguage: "en",
    url: canonical || undefined,
    about: guide.service && typeof guide.service === "object"
      ? { "@type": "Service", name: guide.service.title }
      : undefined,
  };
  return _prune(schema);
}

function buildFaqSchema(faq) {
  if (!Array.isArray(faq) || faq.length < 2) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq
      .filter((f) => f?.question && f?.answer)
      .map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
  };
}

function _prune(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v == null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    out[k] = v;
  }
  return out;
}
